import React, { useState } from 'react';
import { MarketerCard } from './AssignmentBoardCard';
import { AssignmentHistoryPanel } from './AssignmentHistoryPanel';
import { UnassignedConsultantPool } from './UnassignedConsultantPool';
import { countByToday, todayLabel } from './assignmentText';

const S = {
  wrap: { padding: 12, display: 'flex', flexDirection: 'column', gap: 10 },
  boardTop: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap' },
  stats: { display: 'flex', gap: 7, flexWrap: 'wrap' },
  statBasis: { alignSelf: 'center', fontSize: 12, fontWeight: 800, color: 'var(--text-muted)', marginRight: 2 },
  stat: { minWidth: 78, border: '1px solid var(--border)', borderRadius: 8, padding: '6px 9px', background: 'var(--bg-subtle)' },
  statLabel: { fontSize: 10, color: 'var(--text-muted)', fontWeight: 900 },
  statValue: { marginTop: 2, fontSize: 14, color: 'var(--text)', fontWeight: 950 },
  workbench: { display: 'grid', gridTemplateColumns: '240px minmax(0,1fr)', gap: 10, alignItems: 'start' },
  leftRail: { display: 'flex', flexDirection: 'column', gap: 8, minWidth: 0 },
  marketerGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(260px,1fr))', gap: 10 },
  smallBtn: {
    height: 28,
    border: '1px solid var(--border)',
    borderRadius: 8,
    padding: '0 8px',
    background: 'var(--bg-card)',
    color: 'var(--text-sub)',
    fontSize: 11,
    fontWeight: 850,
    cursor: 'pointer',
  },
  empty: { padding: 18, textAlign: 'center', color: 'var(--text-muted)', fontSize: 12 },
  endDrop: (active) => ({
    border: active ? '2px solid #dc2626' : '1px dashed rgba(220,38,38,0.45)',
    borderRadius: 10,
    padding: '9px 11px',
    background: active ? 'rgba(220,38,38,0.12)' : 'rgba(220,38,38,0.06)',
    color: '#dc2626',
    fontSize: 12,
    fontWeight: 950,
    minHeight: 36,
    display: 'flex',
    alignItems: 'center',
  }),
  hidden: { display: 'flex', gap: 7, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end' },
  hiddenLabel: { fontSize: 11, color: 'var(--text-muted)', fontWeight: 900 },
  viewTabs: { display: 'flex', gap: 4, padding: 4, border: '1px solid var(--border)', borderRadius: 9, background: 'var(--bg-subtle)' },
  viewTab: (active) => ({
    height: 28,
    border: 'none',
    borderRadius: 7,
    padding: '0 10px',
    background: active ? 'var(--bg-card)' : 'transparent',
    color: active ? 'var(--text)' : 'var(--text-muted)',
    fontSize: 11,
    fontWeight: 950,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  }),
};

export function AssignmentBoard({
  model,
  assignments,
  today,
  onAdd,
  onOpen,
  onRequestAssign,
  onMove,
  onEnd,
  saving,
}) {
  const [viewMode, setViewMode] = useState('current');
  const [dragItem, setDragItem] = useState(null);
  const [dragOverMarketerId, setDragOverMarketerId] = useState(null);
  const [endDropActive, setEndDropActive] = useState(false);
  // 기준 달·숨김·배정 안 된 직원·건수 계산은 페이지가 useAssignmentBoardModel 로 한 번 하고 넘긴다(휴대폰 화면·마케터 고르기 창과 공유).
  const {
    billingRange,
    currentAssignments,
    allMarketers,
    visibleMarketers,
    hiddenMarketers,
    unassignedConsultants,
    toggleHide,
  } = model;

  function clearDrag() {
    setDragItem(null);
    setDragOverMarketerId(null);
    setEndDropActive(false);
  }

  function handleMarketerDrop(marketer) {
    const item = dragItem;
    clearDrag();
    if (!item) return;
    if (item.type === 'assignment') onMove?.(item.assignment, marketer);
    if (item.type === 'consultant') onAdd?.(marketer, item.consultant);
  }

  function handleEndDrop(event) {
    if (dragItem?.type !== 'assignment') return;
    event.preventDefault();
    const assignment = dragItem.assignment;
    clearDrag();
    onEnd?.(assignment);
  }

  if (!allMarketers.length) return <div style={S.empty}>마케터가 없습니다.</div>;

  // 위쪽 숫자 = 오늘 기준(배지와 같은 판정) — 이번 달 안에 이미 끝난 배정은 '이번 달 끝남'으로 따로 센다
  const todayCounts = countByToday(currentAssignments, today);

  return (
    <div style={S.wrap}>
      <div style={S.boardTop}>
        <div style={S.stats}>
          <div style={S.statBasis}>오늘({todayLabel(today)}) 기준</div>
          <div style={S.stat}><div style={S.statLabel}>근무 중</div><div style={S.statValue}>{todayCounts.active}</div></div>
          <div style={S.stat}><div style={S.statLabel}>시작 예정</div><div style={S.statValue}>{todayCounts.upcoming}</div></div>
          {todayCounts.ended > 0 && (
            <div style={S.stat}><div style={S.statLabel}>이번 달 끝남</div><div style={S.statValue}>{todayCounts.ended}</div></div>
          )}
          <div style={S.stat}><div style={S.statLabel}>배정 안 된 직원</div><div style={S.statValue}>{unassignedConsultants.length}</div></div>
        </div>
        <div style={S.hidden}>
          <div style={S.viewTabs}>
            <button type="button" style={S.viewTab(viewMode === 'current')} onClick={() => setViewMode('current')}>진행 중</button>
            <button type="button" style={S.viewTab(viewMode === 'history')} onClick={() => setViewMode('history')}>끝난 배정</button>
          </div>
          {hiddenMarketers.length > 0 && (
            <>
              <span style={S.hiddenLabel}>숨긴 마케터</span>
              {hiddenMarketers.map((marketer) => (
                <button key={marketer.id} type="button" style={S.smallBtn} onClick={() => toggleHide(marketer.id)}>
                  {marketer.name} 보이기
                </button>
              ))}
            </>
          )}
        </div>
      </div>

      {viewMode === 'history' ? (
        <AssignmentHistoryPanel assignments={assignments} />
      ) : (
        <div style={S.workbench}>
          <div style={S.leftRail}>
            <UnassignedConsultantPool
              consultants={unassignedConsultants}
              basisLabel={`이번 달(${billingRange.label.replace(/^\d+년 /, '')})`}
              saving={saving}
              onDragStart={(consultant) => setDragItem({ type: 'consultant', consultant })}
              onDragEnd={clearDrag}
              onPick={onRequestAssign}
            />
            {dragItem?.type === 'assignment' && (
              <div
                style={S.endDrop(endDropActive)}
                onDragOver={(event) => {
                  event.preventDefault();
                  setEndDropActive(true);
                }}
                onDragLeave={() => setEndDropActive(false)}
                onDrop={handleEndDrop}
              >
                여기에 놓으면 {dragItem.assignment.consultant_name} 배정 종료
              </div>
            )}
          </div>
          <div style={{ minWidth: 0 }}>
            <div style={S.marketerGrid}>
              {visibleMarketers.map((marketer) => (
                <MarketerCard
                  key={marketer.id}
                  marketer={marketer}
                  rows={currentAssignments.filter((row) => Number(row.marketer_user_id) === marketer.id)}
                  range={billingRange}
                  today={today}
                  onAdd={onAdd}
                  onOpen={onOpen}
                  onHide={toggleHide}
                  onDropItem={handleMarketerDrop}
                  onDragStart={setDragItem}
                  onDragEnd={clearDrag}
                  dragItem={dragItem}
                  dragOverMarketerId={dragOverMarketerId}
                  setDragOverMarketerId={setDragOverMarketerId}
                  saving={saving}
                />
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
